import React, { useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import titleIcon from '@/assets/images/title_icon.png';
import { DatePicker, Dropdown, Empty, Select } from '@maxtropy/components';
import titleBg from '../../imgs/titleBg.png';
import { Progress } from 'antd';
import blueSpin from '../../imgs/blueSpin.png';
import yellowSpin from '../../imgs/yellowSpin.png';
import iconVocsExhaustPort from '../../imgs/icon-VocsExhaustPort.png';
import iconSewageStation from '../../imgs/icon-sewageStation.png';
import SwitchDate, {
  IndexTimeActive,
} from '@/pages/WgqAllMaps/components/UnityEnergyCockpitMap/components/LeftBox/components/SwitchDate';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import { keepTwoAndWanOrLine, keepTwoNull } from '@/utils/utils';
import {
  apiV2EnvMonitorConfigGetPost,
  apiV2EnvMonitorDashboardInstantAvgPost,
  apiV2EnvMonitorDashboardOverviewPost,
  apiV2EnvScreenAreaSummaryPost,
} from '@maxtropy/device-customer-apis-v2';

const parsePercent = (ratio: string | number) => Number(String(ratio).replace('%', '')) || 0;
const formatMetric = (value?: number, unit?: string) => {
  if (value === undefined || value === null) return '--';
  return `${keepTwoAndWanOrLine(value)}${unit ?? ''}`;
};
const formatIndicatorValue = (value?: number) => keepTwoAndWanOrLine(value);
const formatPercent = (value?: number) => (value === undefined || value === null ? '--' : `${keepTwoNull(value)}%`);
const getOverviewRange = (activeKey: IndexTimeActive, value: Dayjs) => {
  const now = dayjs();
  const isMonth = activeKey === IndexTimeActive.THIS_MONTH;
  if (isMonth) {
    const start = value.startOf('month');
    const isCurrent = value.isSame(now, 'month');
    const end = isCurrent ? now : value.endOf('month');
    return {
      granularity: 'M',
      startTime: start.valueOf(),
      endTime: end.valueOf(),
    };
  }

  const start = value.startOf('year');
  const isCurrent = value.isSame(now, 'year');
  const end = isCurrent ? now : value.endOf('year');
  return {
    granularity: 'Y',
    startTime: start.valueOf(),
    endTime: end.valueOf(),
  };
};
const getInstantAvgRange = (value: Dayjs) => {
  const now = dayjs();
  const start = value.startOf('day');
  const isCurrent = value.isSame(now, 'day');
  const end = isCurrent ? now : value.endOf('day');
  return {
    startTime: start.valueOf(),
    endTime: end.valueOf(),
  };
};

interface LeftPanelProps {
  isFullScreen?: boolean;
}

const LeftPanel: React.FC<LeftPanelProps> = () => {
  const { data: monitorConfig } = useRequest(() => apiV2EnvMonitorConfigGetPost({}));
  const [selectedGasDeviceId, setSelectedGasDeviceId] = useState<string>();
  const [selectedWaterDeviceId, setSelectedWaterDeviceId] = useState<string>();
  const [overviewActiveKey, setOverviewActiveKey] = useState<IndexTimeActive>(IndexTimeActive.THIS_MONTH);
  const [overviewDate, setOverviewDate] = useState<Dayjs>(dayjs());
  const [gasDate, setGasDate] = useState<Dayjs>(dayjs());

  const { gasDevices, gasMonitorOptions, waterDevices, waterMonitorOptions } = useMemo(() => {
    const gas = monitorConfig?.exhaustDevices ?? [];
    const water = monitorConfig?.wastewaterDevices ?? [];
    const toOptions = (list: typeof gas) =>
      list.map(item => ({
        label: item.deviceName ?? `设备-${item.deviceId}`,
        value: String(item.deviceId),
      }));

    return {
      gasDevices: gas,
      gasMonitorOptions: toOptions(gas),
      waterDevices: water,
      waterMonitorOptions: toOptions(water),
    };
  }, [monitorConfig]);

  useEffect(() => {
    if (!selectedGasDeviceId && gasDevices.length) {
      setSelectedGasDeviceId(String(gasDevices[0].deviceId));
    }
  }, [gasDevices, selectedGasDeviceId]);

  useEffect(() => {
    if (!selectedWaterDeviceId && waterDevices.length) {
      setSelectedWaterDeviceId(String(waterDevices[0].deviceId));
    }
  }, [selectedWaterDeviceId, waterDevices]);

  const currentGasDevice = useMemo(() => {
    if (!gasDevices.length) return undefined;
    return gasDevices.find(item => String(item.deviceId) === selectedGasDeviceId) ?? gasDevices[0];
  }, [gasDevices, selectedGasDeviceId]);

  const currentWaterDevice = useMemo(() => {
    if (!waterDevices.length) return undefined;
    return waterDevices.find(item => String(item.deviceId) === selectedWaterDeviceId) ?? waterDevices[0];
  }, [waterDevices, selectedWaterDeviceId]);

  const { data: overviewData } = useRequest(
    () => {
      const { granularity, startTime, endTime } = getOverviewRange(overviewActiveKey, overviewDate);
      return apiV2EnvMonitorDashboardOverviewPost({
        granularity,
        startTime,
        endTime,
      });
    },
    { refreshDeps: [overviewActiveKey, overviewDate.valueOf()] }
  );
  const { data: areaSummary } = useRequest(() => apiV2EnvScreenAreaSummaryPost({}));

  const { data: gasInstantAvg } = useRequest(
    async () => {
      if (!currentGasDevice?.deviceId) return undefined;
      const { startTime, endTime } = getInstantAvgRange(gasDate);
      return apiV2EnvMonitorDashboardInstantAvgPost({
        deviceId: currentGasDevice.deviceId,
        deviceType: 1,
        startTime,
        endTime,
      });
    },
    {
      refreshDeps: [currentGasDevice?.deviceId, gasDate.valueOf()],
    }
  );

  const { data: waterInstantAvg } = useRequest(
    async () => {
      if (!currentWaterDevice?.deviceId) return undefined;
      const { startTime, endTime } = getInstantAvgRange(gasDate);
      return apiV2EnvMonitorDashboardInstantAvgPost({
        deviceId: currentWaterDevice.deviceId,
        deviceType: 2,
        startTime,
        endTime,
      });
    },
    {
      refreshDeps: [currentWaterDevice?.deviceId, gasDate.valueOf()],
    }
  );

  const gasIndicatorRows = useMemo(
    () =>
      (gasInstantAvg?.indicators ?? []).map(item => ({
        title: `${item.propertyName ?? '--'}${item.unit ? `（${item.unit}）` : ''}`,
        avg: formatIndicatorValue(item.hourlyAvg),
        upper: formatIndicatorValue(item.upperLimit),
        ratio: formatPercent(item.ratio),
      })),
    [gasInstantAvg]
  );

  const waterIndicatorRows = useMemo(
    () =>
      (waterInstantAvg?.indicators ?? []).map(item => ({
        title: `${item.propertyName ?? '--'}${item.unit ? `（${item.unit}）` : ''}`,
        avg: formatIndicatorValue(item.hourlyAvg),
        upper: formatIndicatorValue(item.upperLimit),
        ratio: formatPercent(item.ratio),
      })),
    [waterInstantAvg]
  );

  const gasSummaryCards = useMemo(
    () => [
      {
        label: '废气排放总量',
        value: formatMetric(
          overviewData?.exhaustIndicators?.exhaustVolume?.value,
          overviewData?.exhaustIndicators?.exhaustVolume?.unit
        ),
      },
      {
        label: '氮氧化物排放量',
        value: formatMetric(
          overviewData?.exhaustIndicators?.noxEmission?.value,
          overviewData?.exhaustIndicators?.noxEmission?.unit
        ),
      },
      {
        label: '非甲烷总烃排放量',
        value: formatMetric(
          overviewData?.exhaustIndicators?.nmhcEmission?.value,
          overviewData?.exhaustIndicators?.nmhcEmission?.unit
        ),
      },
    ],
    [overviewData]
  );

  const waterSummaryCards = useMemo(
    () => [
      {
        label: '氨氮排放量',
        value: formatMetric(
          overviewData?.wastewaterIndicators?.ammoniaNitrogenEmission?.value,
          overviewData?.wastewaterIndicators?.ammoniaNitrogenEmission?.unit
        ),
      },
      {
        label: '总磷排放量',
        value: formatMetric(
          overviewData?.wastewaterIndicators?.totalPhosphorusEmission?.value,
          overviewData?.wastewaterIndicators?.totalPhosphorusEmission?.unit
        ),
      },
      {
        label: 'COD排放量',
        value: formatMetric(
          overviewData?.wastewaterIndicators?.codEmission?.value,
          overviewData?.wastewaterIndicators?.codEmission?.unit
        ),
      },
      {
        label: '总氮排放量',
        value: formatMetric(
          overviewData?.wastewaterIndicators?.totalNitrogenEmission?.value,
          overviewData?.wastewaterIndicators?.totalNitrogenEmission?.unit
        ),
      },
      {
        label: '废水排放量',
        value: formatMetric(
          overviewData?.wastewaterIndicators?.wastewaterVolume?.value,
          overviewData?.wastewaterIndicators?.wastewaterVolume?.unit
        ),
      },
    ],
    [overviewData]
  );

  return (
    <div className={styles.leftPanel}>
      <section className={styles.topCard}>
        <div className={styles.block}>
          <div className={styles.blockTitle}>
            <img src={titleIcon} alt="" />
            <span>废气</span>
            <div className={styles.rightAlign}>
              <Dropdown
                type="link"
                className={styles.dropdownWrapper}
                getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
                trigger={['click']}
                dropdownRender={() => (
                  <div className={styles.dropdownMenuWrapper}>
                    <SwitchDate
                      activeKey={overviewActiveKey}
                      value={overviewDate}
                      setActiveKey={setOverviewActiveKey}
                      setValue={value => value && setOverviewDate(value)}
                    />
                  </div>
                )}
              >
                {dayjs(overviewDate).format(overviewActiveKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY')}
              </Dropdown>
            </div>
          </div>
          <div className={styles.summaryGrid}>
            {gasSummaryCards.map(item => (
              <div key={`gas-${item.label}`} className={styles.summaryItem}>
                <div className={styles.summaryLabel}>{item.label}</div>
                <div className={styles.summaryValue}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.block}>
          <div className={styles.blockTitle}>
            <img src={titleIcon} alt="" />
            <span>废水</span>
          </div>
          <div className={styles.summaryGrid}>
            {waterSummaryCards.map(item => (
              <div key={`water-${item.label}`} className={styles.summaryItem}>
                <div className={styles.summaryLabel}>{item.label}</div>
                <div className={`${styles.summaryValue} ${styles.warningValue}`}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.bottomCard}>
        <div className={styles.block}>
          <div className={styles.blockTitle}>
            <img src={titleIcon} alt="" />
            <span>废气指标</span>
            <div className={styles.selectWrap}>
              <Select
                size="small"
                style={{ width: 120 }}
                value={selectedGasDeviceId ?? (gasMonitorOptions[0]?.value as string | undefined)}
                options={gasMonitorOptions}
                placeholder="请选择设备"
                getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
                onChange={value => setSelectedGasDeviceId(String(value))}
              />
            </div>
            <div className={styles.rightAlign}>
              <DatePicker
                allowClear={false}
                value={gasDate}
                size="small"
                onChange={value => value && setGasDate(value)}
                disabledDate={current => !!current && current > dayjs().endOf('day')}
                getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
              />
            </div>
          </div>
          <div className={styles.indicatorList}>
            {gasIndicatorRows.length ? (
              gasIndicatorRows.map((row, idx) => (
                <div key={`indicator-gas-${idx}`} className={styles.indicatorItem}>
                  <div className={styles.indicatorHead} style={{ backgroundImage: `url(${titleBg})` }}>
                    {row.title}
                  </div>
                  <div className={styles.indicatorBody}>
                    <div className={styles.indicatorMetric}>
                      <span>小时均值:</span>
                      <span className={styles.indicatorValue}>{row.avg}</span>
                    </div>
                    <div className={styles.indicatorMetric}>
                      <span>小时均值上限:</span>
                      <span className={styles.indicatorValue}>{row.upper}</span>
                    </div>
                    <div className={styles.progressRow}>
                      <span>占比:</span>
                      <div className={styles.progressCircle}>
                        <Progress
                          percent={parsePercent(row.ratio)}
                          trailColor="rgba(74,144,226,0.2)"
                          showInfo={false}
                          strokeColor="#57FB8B"
                        />
                      </div>
                      <span className={styles.percent}>{row.ratio}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.emptyWrap}>
                <Empty />
              </div>
            )}
          </div>
        </div>

        <div className={styles.block}>
          <div className={styles.blockTitle}>
            <img src={titleIcon} alt="" />
            <span>废水指标</span>
            <div className={styles.selectWrap}>
              <Select
                size="small"
                style={{ width: 120 }}
                value={selectedWaterDeviceId ?? (waterMonitorOptions[0]?.value as string | undefined)}
                options={waterMonitorOptions}
                placeholder="请选择设备"
                getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
                onChange={value => setSelectedWaterDeviceId(String(value))}
              />
            </div>
          </div>
          <div className={styles.waterIndicatorList}>
            {waterIndicatorRows.length ? (
              waterIndicatorRows.map((item, idx) => (
                <div key={`indicator-water-${idx}`} className={styles.indicatorItem}>
                  <div className={styles.indicatorHead} style={{ backgroundImage: `url(${titleBg})` }}>
                    {item.title}
                  </div>
                  <div className={styles.indicatorBody}>
                    <div className={styles.indicatorMetric}>
                      <span>小时均值:</span>
                      <span className={`${styles.indicatorValue} ${styles.warningValue}`}>{item.avg}</span>
                    </div>
                    <div className={styles.indicatorMetric}>
                      <span>小时均值上限:</span>
                      <span className={`${styles.indicatorValue} ${styles.warningValue}`}>{item.upper}</span>
                    </div>
                    <div className={styles.progressRow}>
                      <span>占比:</span>
                      <div className={styles.progressCircle}>
                        <Progress
                          percent={parsePercent(item.ratio)}
                          trailColor="rgba(74,144,226,0.2)"
                          showInfo={false}
                          strokeColor="#57FB8B"
                        />
                      </div>
                      <span className={styles.percent}>{item.ratio}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.emptyWrap}>
                <Empty />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className={styles.footerPanel}>
        <div className={styles.footerItem}>
          <div className={styles.footerIconWrap}>
            <img className={styles.footerSpin} src={blueSpin} alt="" />
            <img className={styles.footerIcon} src={iconVocsExhaustPort} alt="" />
          </div>
          <div className={styles.footerText}>
            <div className={styles.footerTitle}>VOCS出气口数量</div>
            <div className={styles.footerValue}>{areaSummary?.vocsDeviceCount ?? '--'}</div>
          </div>
        </div>
        <div className={styles.footerDivider} />
        <div className={styles.footerItem}>
          <div className={styles.footerIconWrap}>
            <img className={styles.footerSpin} src={yellowSpin} alt="" />
            <img className={styles.footerIcon} src={iconSewageStation} alt="" />
          </div>
          <div className={styles.footerText}>
            <div className={styles.footerTitle}>污水站环保数量</div>
            <div className={styles.footerValue}>{areaSummary?.wasteWaterDeviceCount ?? '--'}</div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeftPanel;
